/**
 * API utility functions for making requests to the backend
 * Mimics the functionality of the apiRequest function from the vanilla JS frontend
 */

// Base URL for API requests - points to the Flask backend
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// CSRF token cache
let csrfToken = null;

/**
 * Get CSRF token from the server
 * @returns {Promise<string>} - CSRF token
 */
export const getCsrfToken = async () => {
  if (!csrfToken) {
    try {
      const response = await fetch(`${BASE_URL}/api/auth/csrf-token`, {
        credentials: 'include'
      });
      const data = await response.json();
      csrfToken = data.csrf_token;
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
      // Continue without CSRF token - server will reject but we don't want to block
    }
  }
  return csrfToken;
};

/**
 * Clear cached CSRF token (call on logout)
 */
export const clearCsrfToken = () => {
  csrfToken = null;
};

/**
 * Make an API request to the specified endpoint
 *
 * @param {string} endpoint - The API endpoint to request
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} body - Request body (for POST/PUT requests)
 * @returns {Promise<any>} - Response data
 */
export const apiRequest = async (endpoint, method = "GET", body = null) => {
  const url = `${BASE_URL}${endpoint}`;

  const options = {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    // Include credentials to ensure cookies are sent with requests
    credentials: "include",
  };

  // Add CSRF token for state-changing requests
  if (method !== "GET" && method !== "HEAD") {
    const token = await getCsrfToken();
    if (token) {
      options.headers["X-CSRFToken"] = token;
    }
  }

  if (body) {
    options.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, options);
    
    if (!response.ok) {
      if (response.status === 401 && endpoint === '/api/auth/current_user') {
        return null;
      }
      
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: `HTTP error! status: ${response.status}` };
      }
      
      console.error("API Error:", errorData);
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    if (response.status === 204 || response.headers.get("content-length") === "0") {
      return null;
    }
    
    return await response.json();
  } catch (error) {
    throw error;
  }
}; 

/**
 * Stream an API request that uses SSE from the backend.
 *
 * @param {string} endpoint - The API endpoint to request (e.g., '/api/chat/.../messages/stream').
 * @param {Object} body - Request body for the POST request.
 * @param {AbortSignal} [signal] - Optional AbortSignal to cancel the stream.
 * @returns {AsyncGenerator<string>} Yields text chunks as they arrive.
 */
export async function* streamApiRequest(endpoint, body, signal) {
  const url = `${BASE_URL}${endpoint}`;

  // Get CSRF token for POST request
  const token = await getCsrfToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['X-CSRFToken'] = token;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: headers,
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (part.startsWith('data: ')) {
        const jsonStr = part.slice('data: '.length);
        try {
          const parsed = JSON.parse(jsonStr);
          if (parsed.text !== undefined) {
            yield parsed.text;
          } else if (parsed.session_title !== undefined) {
            yield { session_title: parsed.session_title };
          } else if (parsed.error) {
            console.error('streamApiRequest: Received error in stream data:', parsed.error);
          }
        } catch (err) {
          console.error('streamApiRequest: Failed to parse SSE chunk - original string was:', jsonStr, 'Error:', err);
        }
      }
    }
    buffer = parts[parts.length - 1];
  }
  if (buffer.startsWith('data: ')) {
    const jsonStr = buffer.slice('data: '.length);
    try {
      const parsed = JSON.parse(jsonStr);
      if (parsed.text !== undefined) {
        yield parsed.text;
      } else if (parsed.session_title !== undefined) {
        yield { session_title: parsed.session_title };
      } else if (parsed.error) {
        console.error('streamApiRequest: Received error in final buffered data:', parsed.error);
      }
    } catch (err) {
      console.error('streamApiRequest: Failed to parse final SSE buffer - original string was:', jsonStr, 'Error:', err);
    }
  }
}

/**
 * Validate image file before upload
 *
 * @param {File} file - Image file to validate
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export function validateImageFile(file) {
  // Check file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type: ${file.type}. Allowed: JPEG, PNG, WebP, GIF`
    };
  }

  // Check file size (5MB max)
  const maxSizeMB = 5;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `File too large: ${fileSizeMB}MB (max ${maxSizeMB}MB)`
    };
  }

  return { valid: true, error: null };
}

/**
 * Convert File object to base64 with metadata
 *
 * @param {File} file - Image file
 * @returns {Promise<Object>} - Object with base64 and filename
 */
export function fileToBase64WithMetadata(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      // Remove data URI prefix (data:image/jpeg;base64,)
      const base64 = reader.result.split(',')[1];

      resolve({
        base64: base64,
        filename: file.name
      });
    };

    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`));
    };

    reader.readAsDataURL(file);
  });
} 